import React, { useEffect, useMemo, useRef, useState } from "react";
import LpInput from "./LpInput";
import { Plus, Close } from "./LpIcons";
import useAddressAutocomplete from "../../../common/useAddressAutocomplete";
import { LocationIcon, WarehouseIcon } from "../../../common/CustomIcon";
import { alertMessage } from "../../../common/Common";

/** Warehouse rows use the warehouse glyph; predefined + web use the map pin. */
const SearchResultIcon = ({ item, kind }) => {
  const isWh = kind === "predefined" && item.l_type === "WH";
  if (isWh) {
    return (
      <span className="oc-search__bullet oc-search__bullet--bg" aria-hidden="true">
        <WarehouseIcon alt="" width={16} height={16} />
      </span>
    );
  }
  return (
    <span className="oc-search__bullet oc-search__bullet--bg" aria-hidden="true">
      <LocationIcon alt="" width={16} height={16} />
    </span>
  );
};

/**
 * Linchpin-styled address-autocomplete used in v3 leg cards.
 *
 * Composes the shared `useAddressAutocomplete` hook with a Linchpin dropdown UI
 * matching the v3 prototype: grouped sections ("Predefined Locations" / "Web
 * Results"), avatar bullet, optional "Add New '<typed>'" CTA. Existing v1/v2
 * callers continue to use SearchAddress_v2 (antd-styled), which consumes the
 * same hook — so the search logic lives in exactly one place.
 *
 * Props:
 *  - value, onChange — controlled text (parent owns the string).
 *  - onSelect(item) — fires when the user picks a row.
 *  - mode: "name" | "address" | "none" — toggles which sources are queried.
 *     · "name"    → only predefined locations (web hidden)
 *     · "address" → only web results (predefined hidden)
 *     · "none"    → no suggestions at all (plain input, e.g. linehaul WH legs
 *        where the street address is filled from the chosen warehouse)
 *  - showAddNew + onAddNew — append "Add New '<typed>'" row at the bottom.
 *  - placeholder, disabled — forwarded to the input.
 *  - error — shows lp-input--error border.
 *  - orderTypeConfig, selectedWarehouseId, linehaul_code, linehaul_label —
 *     forwarded to the hook so predefined search respects warehouse scoping.
 */
function LpAddressSearch({
  value,
  onChange,
  onSelect,
  mode = "name",
  placeholder,
  disabled,
  error,
  showAddNew = false,
  onAddNew,
  orderTypeConfig,
  selectedWarehouseId,
  linehaul_code,
  linehaul_label,
  disableBrowserAutofill = true,
  name,
  // Optional adornment shown inside the input (e.g. a search-glyph icon
  // on the top-level address picker). Forwarded straight to LpInput.
  prefix,
  dataTestId,
  containerDataTestId,
  addNewTestId,
}) {
  const hidePredefined = mode === "address" || mode === "none";
  const hideWebResults = mode === "name" || mode === "none";
  const { results, loading, onSearch, resolveSelection } = useAddressAutocomplete({
    hidePredefined,
    hideWebResults,
    orderTypeConfig,
    selectedWarehouseId,
    linehaul_code,
    linehaul_label,
  });

  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const ref = useRef(null);
  const ddRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const handleChange = (e) => {
    const v = e.target.value;
    if (onChange) onChange(v);
    onSearch(v);
    setOpen(true);
    setActiveIdx(-1);
  };

  const handleSelect = async (item) => {
    setOpen(false);
    setActiveIdx(-1);
    // Cost-optimized flow: web rows are prediction-only, so fetch Place Details
    // for just the chosen row here. resolveSelection is a no-op for predefined
    // rows and for detailed-mode rows that already carry l_address.
    const resolved = await resolveSelection(item);
    // Null means a web row's Place Details couldn't be fetched — surface it
    // rather than passing an item with no l_address downstream.
    if (!resolved) {
      alertMessage(["Couldn't fetch address details. Please try again."], "error", 3);
      return;
    }
    if (onSelect) onSelect(resolved);
  };

  const handleAddNew = () => {
    if (onAddNew) onAddNew(value || "");
    setOpen(false);
    setActiveIdx(-1);
  };

  // Close the dropdown when focus leaves the whole component (e.g. Tab to the
  // next field). The outside-mousedown listener above only covers pointer
  // interactions; keyboard focus-out produces no mousedown, so without this the
  // popover stayed open after tabbing away. Ignore focus moving to a child
  // (an option row or the clear button) so those interactions don't close it.
  const handleBlur = (e) => {
    if (ref.current && ref.current.contains(e.relatedTarget)) return;
    setOpen(false);
    setActiveIdx(-1);
  };

  // Clear button on the input (only visible when there's text). Resets the
  // parent value, clears the dropdown, closes the popover.
  const handleClear = () => {
    if (onChange) onChange("");
    onSearch("");
    setOpen(false);
    setActiveIdx(-1);
  };

  const predefined = results.filter((r) => r.type === "predefined");
  const web = results.filter((r) => r.type === "web");
  const hasAny = loading || predefined.length > 0 || web.length > 0;
  const showAddRow = showAddNew && !!value && !loading;
  // Show an explicit empty row when the user has typed something but neither
  // source returned anything (and we aren't offering "Add New"). Without this
  // the entire dropdown was suppressed and the user got no feedback.
  const showNoResults = !loading && !hasAny && !showAddRow && !!value;

  // Flat list of keyboard-pickable rows in render order — used for Up/Down
  // navigation. Group headers + the "loading" placeholder are skipped.
  const pickables = useMemo(() => {
    if (loading) return [];
    const out = [];
    predefined.forEach((it) => out.push({ kind: "predefined", item: it }));
    web.forEach((it) => out.push({ kind: "web", item: it }));
    if (showAddRow) out.push({ kind: "add" });
    return out;
  }, [loading, predefined, web, showAddRow]);

  // Reset focus when the option set changes (new search, results arrived).
  useEffect(() => { setActiveIdx(-1); }, [pickables.length]);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      if (!pickables.length) return;
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => (i + 1) % pickables.length);
    } else if (e.key === "ArrowUp") {
      if (!pickables.length) return;
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => (i <= 0 ? pickables.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      if (!open || activeIdx < 0 || !pickables[activeIdx]) return;
      e.preventDefault();
      const p = pickables[activeIdx];
      if (p.kind === "add") handleAddNew();
      else handleSelect(p.item);
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setOpen(false);
        setActiveIdx(-1);
      }
    }
  };

  // Auto-scroll the focused row into view inside the scrollable dropdown.
  useEffect(() => {
    if (activeIdx < 0 || !ddRef.current) return;
    const node = ddRef.current.querySelector(`[data-idx="${activeIdx}"]`);
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ block: "nearest" });
    }
  }, [activeIdx]);

  return (
    <div className="oc-search" ref={ref} onBlur={handleBlur} data-testid={containerDataTestId}>
      <LpInput
        value={value || ""}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        name={name}
        disableBrowserAutofill={disableBrowserAutofill}
        prefix={prefix}
        data-testid={dataTestId}
        role="combobox"
        aria-expanded={open}
        aria-controls="oc-search-dd"
        aria-activedescendant={activeIdx >= 0 ? `oc-search-opt-${activeIdx}` : undefined}
        className={value ? "oc-search__input--has-clear" : ""}
      />
      {value && !disabled && (
        <button
          type="button"
          tabIndex={-1}
          className="oc-search__clear"
          aria-label="Clear"
          onMouseDown={(e) => { e.preventDefault(); handleClear(); }}
        >
          <Close width={14} height={14} />
        </button>
      )}
      {open && (hasAny || showAddRow || showNoResults) && (
        <div className="oc-search__dd" id="oc-search-dd" role="listbox" ref={ddRef}>
          {loading && <div className="oc-search__empty">Searching…</div>}
          {showNoResults && <div className="oc-search__empty">No results found</div>}
          {!loading && predefined.length > 0 && (
            <div className="oc-search__group">Predefined Locations</div>
          )}
          {!loading && predefined.map((it, i) => {
            const idx = i; // predefined occupy indices [0..predefined.length)
            const active = idx === activeIdx;
            return (
              <button
                key={`p-${it.id}`}
                type="button"
                tabIndex={-1}
                role="option"
                id={`oc-search-opt-${idx}`}
                data-idx={idx}
                aria-selected={active}
                className={`oc-search__opt${active ? " oc-search__opt--active" : ""}`}
                onMouseDown={() => handleSelect(it)}
                onMouseEnter={() => setActiveIdx(idx)}
              >
                <SearchResultIcon item={it} kind="predefined" />
                <span className="oc-search__opt-text">
                  <span className="oc-search__name">{displayName(it)}</span>
                  <span className="oc-search__sub">{formatAddr(it.l_address)}</span>
                </span>
              </button>
            );
          })}
          {!loading && web.length > 0 && (
            <div className="oc-search__group">Web Results</div>
          )}
          {!loading && web.map((it, i) => {
            const idx = predefined.length + i;
            const active = idx === activeIdx;
            return (
              <button
                key={`w-${it.id}`}
                type="button"
                tabIndex={-1}
                role="option"
                id={`oc-search-opt-${idx}`}
                data-idx={idx}
                aria-selected={active}
                className={`oc-search__opt${active ? " oc-search__opt--active" : ""}`}
                onMouseDown={() => handleSelect(it)}
                onMouseEnter={() => setActiveIdx(idx)}
              >
                <SearchResultIcon item={it} kind="web" />
                <span className="oc-search__opt-text">
                  <span className="oc-search__name">{it.name || it.company_name}</span>
                  <span className="oc-search__sub">{it.address}</span>
                </span>
              </button>
            );
          })}
          {showAddRow && (() => {
            const idx = predefined.length + web.length;
            const active = idx === activeIdx;
            return (
              <button
                type="button"
                tabIndex={-1}
                id={`oc-search-opt-${idx}`}
                data-idx={idx}
                data-testid={addNewTestId}
                aria-selected={active}
                className={`oc-search__opt oc-search__opt--add${active ? " oc-search__opt--active" : ""}`}
                onMouseDown={handleAddNew}
                onMouseEnter={() => setActiveIdx(idx)}
              >
                <Plus width={16} height={16} />
                <span className="oc-search__opt-text">
                  <span className="oc-search__name">
                    Add New '<strong>{value}</strong>'
                  </span>
                </span>
              </button>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// Pick the best display label for a predefined location row.
function displayName(it) {
  if (it.company_name) return it.company_name;
  if (it.name) return it.name;
  const fn = (it.first_name || "").trim();
  const ln = (it.last_name || "").trim();
  return `${fn} ${ln}`.trim();
}

function formatAddr(la) {
  if (!la) return "";
  return [la.address_line1, la.address_line2, la.city, la.state, la.zipcode, la.country]
    .filter(Boolean).join(", ");
}

export default React.memo(LpAddressSearch);
